import { Navigate, RouteObject } from 'react-router-dom';
// Auth pages
import { NotificationsProvider } from '../contexts/NotificationsContext';
import { AppWrapper } from '../layout/AppWrapper';
import ProtectedPermissionsRoute from '../layout/ProtectedPermissionsRoute';
import ProtectedProjectRoute from '../layout/ProtectedProjectRoute';
import { ProtectedRoute } from '../layout/ProtectedRoute';
import { Accounts, AccountsPageRoles } from '../pages/app/Accounts';
import Activities, { ActivitiesPageRoles } from '../pages/app/Activities';
import { Assets, AssetsPageRoles } from '../pages/app/Assets';
import Dashboard, { DashboardPageRoles } from '../pages/app/Dashboard';
import { Depreciation, DepreciationPageRoles } from '../pages/app/Depreciation';
import { EmployeePageRoles, Employees } from '../pages/app/Employees';
import { ProjectChat, ProjectChatPageRoles } from '../pages/app/ProjectChat';
import { Projects } from '../pages/app/Projects';
import Reports, { ReportPageRoles } from '../pages/app/Reports';
import Salaries, { SalariesPageRoles } from '../pages/app/Salaries';
import Settings from '../pages/app/Settings';
import { Summary, SummaryPageRoles } from '../pages/app/Summary';
import Transactions, { TransactionPageRoles } from '../pages/app/Transactions';
import { AuthCallbackPage } from '../pages/auth/AuthCallbackPage';
import AuthPopupBridge from '../pages/auth/AuthPopupBridge';
import ForgotPassword from '../pages/auth/ForgotPassword';
import JoinOrganisation from '../pages/auth/Join';
import Login from '../pages/auth/Login';
import Logout from '../pages/auth/Logout';
import ResetPassword from '../pages/auth/ResetPassword';
import SelectOrganisation from '../pages/auth/SelectOrganisation';
import Signup from '../pages/auth/Signup';
import VerifyEmailPending from '../pages/auth/VerifyEmailPending';
import VerifyEmailSuccess from '../pages/auth/VerifyEmailSuccess';
import Welcome from '../pages/auth/Welcome';
import { Error403, Error404, Error500 } from '../pages/errors';
import Error418 from '../pages/errors/Error418';
import OnboardingOrganisation from '../pages/onboarding/OnboardingOrganisation';
import OnboardingPaymentSubscription from '../pages/onboarding/OnboardingPaymentSubscription';
import OnboardingProject from '../pages/onboarding/OnboardingProject';
import OnboardingWelcome from '../pages/onboarding/OnboardingWelcome';
import OnboardingWrapper from '../pages/onboarding/OnboardingWrapper';
import OnboardingXero from '../pages/onboarding/OnboardingXero';
import { Xero } from '../pages/settings';
import PaymentCards from '../pages/settings/components/PaymentCards';
import Invoices, { InvoiceRoles } from '../pages/settings/Invoices';
import Notifications, { NotificationRoles } from '../pages/settings/Notifications';
import Organisation, { OrganisationRoles } from '../pages/settings/Organisation';
import Profile, { ProfileRoles } from '../pages/settings/Profile';
import Security, { SecurityRoles } from '../pages/settings/Security';
import Plan, { PlanRoles } from '../pages/settings/Plan';
import Team, { TeamRoles } from '../pages/settings/Team';
import { XeroRoles } from '../pages/settings/Xero';

export const routes: RouteObject[] = [
  // Root redirect
  {
    path: '/',
    element: <Navigate to="/app" replace />,
  },
  // dashboard redirect
  {
    path: '/dashboard',
    element: <Navigate to="/app" replace />,
  },

  // Account/Auth routes
  {
    path: '/',
    children: [
      // Public routes
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'signup',
        element: <Signup />,
      },
      {
        path: 'forgot-password',
        element: <ForgotPassword />,
      },
      {
        path: 'reset-password',
        element: <ResetPassword />,
      },
      {
        path: 'logout',
        element: <Logout />,
      },
      {
        path: '/join/:token',
        element: <JoinOrganisation />,
      },
      {
        path: 'verify-email-pending',
        element: <VerifyEmailPending />,
      },
      {
        path: 'verify-email/:token',
        element: <VerifyEmailSuccess />,
      },
      {
        path: 'auth/bridge',
        element: <AuthPopupBridge />,
      },
      // Redirect any auth route when logged in
    ],
  },
  // OAuth callback
  {
    path: '/auth/callback',
    element: <AuthCallbackPage />,
  },

  // Select organisation shouldn't have the app wrapper
  {
    path: '/select-organisation',
    element: (
      <ProtectedRoute>
        <ProtectedPermissionsRoute requireOrganisation={false}>
          <SelectOrganisation />
        </ProtectedPermissionsRoute>
      </ProtectedRoute>
    ),
  },

  // welcome page for new invited users
  {
    path: '/welcome',
    element: (
      <ProtectedRoute>
        <ProtectedPermissionsRoute requireOrganisation={false}>
          <Welcome />
        </ProtectedPermissionsRoute>
      </ProtectedRoute>
    ),
  },

  // Main app routes (protected)
  {
    path: '/app',
    element: (
      <ProtectedRoute>
        <NotificationsProvider>
          <AppWrapper />
        </NotificationsProvider>
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: (
          <ProtectedPermissionsRoute roles={DashboardPageRoles}>
            <Dashboard />
          </ProtectedPermissionsRoute>
        ),
      },
      {
        path: 'projects',
        element: (
          <ProtectedPermissionsRoute roles={ActivitiesPageRoles}>
            <Projects />
          </ProtectedPermissionsRoute>
        ),
      },
      {
        path: 'projects/generate',
        element: (
          <ProtectedPermissionsRoute roles={ProjectChatPageRoles}>
            <ProjectChat />
          </ProtectedPermissionsRoute>
        ),
      },
      {
        path: 'projects/:projectId',
        element: (
          <ProtectedPermissionsRoute roles={ActivitiesPageRoles}>
            <Projects />
          </ProtectedPermissionsRoute>
        ),
      },
      {
        path: 'activities/:projectId',
        element: (
          <ProtectedPermissionsRoute roles={ActivitiesPageRoles}>
            <Activities />
          </ProtectedPermissionsRoute>
        ),
      },
      {
        path: 'activities',
        element: (
          <ProtectedProjectRoute>
            <ProtectedPermissionsRoute roles={ActivitiesPageRoles}>
              <Activities />
            </ProtectedPermissionsRoute>
          </ProtectedProjectRoute>
        ),
      },
      {
        path: 'activities',
        element: (
          <ProtectedProjectRoute>
            <Activities />
          </ProtectedProjectRoute>
        ),
      },
      {
        path: 'transactions/:projectId',
        element: (
          <ProtectedPermissionsRoute roles={TransactionPageRoles}>
            <Transactions />
          </ProtectedPermissionsRoute>
        ),
      },
      {
        path: 'transactions/',
        element: (
          <ProtectedProjectRoute>
            <ProtectedPermissionsRoute roles={TransactionPageRoles}>
              <Transactions />
            </ProtectedPermissionsRoute>
          </ProtectedProjectRoute>
        ),
      },
      {
        path: 'rules',
        element: (
          <ProtectedProjectRoute>
            <ProtectedPermissionsRoute roles={AccountsPageRoles}>
              <Accounts />
            </ProtectedPermissionsRoute>
          </ProtectedProjectRoute>
        ),
      },
      {
        path: 'rules/:projectId',
        element: (
          <ProtectedPermissionsRoute roles={AccountsPageRoles}>
            <Accounts />
          </ProtectedPermissionsRoute>
        ),
      },

      {
        path: 'depreciation/:projectId',
        element: (
          <ProtectedPermissionsRoute roles={DepreciationPageRoles}>
            <Depreciation />
          </ProtectedPermissionsRoute>
        ),
      },
      {
        path: 'depreciation/',
        element: (
          <ProtectedProjectRoute>
            <ProtectedPermissionsRoute roles={DepreciationPageRoles}>
              <Depreciation />
            </ProtectedPermissionsRoute>
          </ProtectedProjectRoute>
        ),
      },
      {
        path: 'reports',
        element: (
          <ProtectedProjectRoute>
            <Reports />
          </ProtectedProjectRoute>
        ),
      },
      {
        path: 'reports/:projectId',
        element: (
          <ProtectedPermissionsRoute roles={ReportPageRoles}>
            <Reports />
          </ProtectedPermissionsRoute>
        ),
      },

      {
        path: 'assets/:projectId',
        element: (
          <ProtectedPermissionsRoute roles={AssetsPageRoles}>
            <Assets />
          </ProtectedPermissionsRoute>
        ),
      },

      {
        path: 'assets/',
        element: (
          <ProtectedProjectRoute>
            <ProtectedPermissionsRoute roles={AssetsPageRoles}>
              <Assets />
            </ProtectedPermissionsRoute>
          </ProtectedProjectRoute>
        ),
      },
      {
        path: 'employees/',
        element: (
          <ProtectedProjectRoute>
            <ProtectedPermissionsRoute roles={EmployeePageRoles}>
              <Employees />
            </ProtectedPermissionsRoute>
          </ProtectedProjectRoute>
        ),
      },

      {
        path: 'employees/:projectId',
        element: (
          <ProtectedPermissionsRoute roles={EmployeePageRoles}>
            <Employees />
          </ProtectedPermissionsRoute>
        ),
      },

      {
        path: 'salaries/',
        element: (
          <ProtectedProjectRoute>
            <ProtectedPermissionsRoute roles={SalariesPageRoles}>
              <Salaries />
            </ProtectedPermissionsRoute>
          </ProtectedProjectRoute>
        ),
      },
      {
        path: 'salaries/:projectId',
        element: <Salaries />,
      },
      {
        path: 'summary/',
        element: (
          <ProtectedProjectRoute>
            <ProtectedPermissionsRoute roles={SummaryPageRoles}>
              <Summary />
            </ProtectedPermissionsRoute>
          </ProtectedProjectRoute>
        ),
      },
      {
        path: 'summary/:projectId',
        element: <Summary />,
      },
    ],
  },

  // Onboarding routes (protected)
  {
    path: '/onboarding',
    element: (
      <ProtectedRoute>
        <OnboardingWrapper />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'organisation',
        element: <OnboardingOrganisation />,
      },
      {
        path: 'project',
        element: <OnboardingProject />,
      },
      {
        path: 'subscription',
        element: <OnboardingPaymentSubscription />,
      },
      {
        path: 'xero',
        element: <OnboardingXero />,
      },
      {
        path: 'welcome',
        element: <OnboardingWelcome />, /// may move outside of this route, so it gets the menu
      },
    ],
  },

  // Settings sub-routes (protected)

  {
    path: 'settings',
    element: (
      <ProtectedRoute>
        <NotificationsProvider>
          <AppWrapper />
        </NotificationsProvider>
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: (
          <ProtectedPermissionsRoute>
            <Settings />
          </ProtectedPermissionsRoute>
        ),
      },
      {
        path: 'profile',
        element: (
          <ProtectedPermissionsRoute roles={ProfileRoles}>
            <Profile />
          </ProtectedPermissionsRoute>
        ),
      },
      {
        path: 'organisation',
        element: (
          <ProtectedPermissionsRoute roles={OrganisationRoles}>
            <Organisation />
          </ProtectedPermissionsRoute>
        ),
      },
      {
        path: 'billing',
        element: (
          <ProtectedPermissionsRoute roles={InvoiceRoles}>
            <PaymentCards />
            <Invoices />
          </ProtectedPermissionsRoute>
        ),
      },
      {
        path: 'notifications',
        element: (
          <ProtectedPermissionsRoute roles={NotificationRoles}>
            <Notifications />
          </ProtectedPermissionsRoute>
        ),
      },
      {
        path: 'security',
        element: (
          <ProtectedPermissionsRoute roles={SecurityRoles}>
            <Security />
          </ProtectedPermissionsRoute>
        ),
      },
      {
        path: 'plan',
        element: (
          <ProtectedPermissionsRoute roles={PlanRoles}>
            <Plan />
          </ProtectedPermissionsRoute>
        ),
      },
      // Back-compat redirect for the old /settings/subscription URL
      {
        path: 'subscription',
        element: <Navigate to="/settings/plan" replace />,
      },
      {
        path: 'team',
        element: (
          <ProtectedPermissionsRoute roles={TeamRoles}>
            <Team />
          </ProtectedPermissionsRoute>
        ),
      },
      {
        path: 'xero',
        element: (
          <ProtectedPermissionsRoute roles={XeroRoles}>
            <Xero />
          </ProtectedPermissionsRoute>
        ),
      },
    ],
  },

  // Errors
  {
    path: '/403',
    element: <Error403 />,
  },
  {
    path: '/500',
    element: <Error500 />,
  },
  {
    path: '/404',
    element: <Error404 />,
  },
  {
    path: '/418',
    element: <Error418 />,
  },

  // 404 fallback
  {
    path: '*',
    element: <Error404 />,
  },
];
